import { is } from 'stdlib/is'

import devOverrides from '../devOverrides/devOverrides'

import isFF              from './utils/isFF'
import isIE              from './utils/isIE'
import getOs             from './utils/getOs'
import isIOS             from './utils/isIOS'
import isEdge            from './utils/isEdge'
import isLinux           from './utils/isLinux'
import isOpera           from './utils/isOpera'
import isApple           from './utils/isApple'
import isSafari          from './utils/isSafari'
import isTablet          from './utils/isTablet'
import isChrome          from './utils/isChrome'
import isMobile          from './utils/isMobile'
import isIPadOS          from './utils/isIPadOS'
import getDevice         from './utils/getDevice'
import isAndroid         from './utils/isAndroid'
import isWindows         from './utils/isWindows'
import getBrowser        from './utils/getBrowser'
import isHandheld        from './utils/isHandheld'
import getOsVersion      from './utils/getOsVersion'
import getBrowserVersion from './utils/getBrowserVersion'

import type { TOs, IOsInfo, TDevice, TBrowser, TOsVersion, IBrowserInfo, TBrowserVersion } from './definitions'

export default class UserAgent {
  private static instance: UserAgent

  private readonly userAgent: string

  /** Os elkérése, userAgent alapján. */
  public get os (): TOs {
    return getOs(this.userAgent)
  }

  /** Os verziószám elkérése. */
  public get osVersion (): TOsVersion {
    return getOsVersion(this.userAgent)
  }

  public get osInfo (): IOsInfo {
    return {
      name: this.os,
      version: this.osVersion
    }
  }

  /** Browser típusának elkérése. */
  public get browser (): TBrowser {
    return getBrowser(this.userAgent)
  }

  /** Browser verziószám elkérése. */
  public get browserVersion (): TBrowserVersion {
    return getBrowserVersion(this.userAgent)
  }

  public get browserInfo (): IBrowserInfo {
    return {
      name: this.browser,
      version: this.browserVersion
    }
  }

  /** Device típusának elkérése. */
  public get device (): TDevice {
    return getDevice(this.userAgent)
  }

  /** Az OS Linux? */
  public get isLinux (): boolean {
    return isLinux(this.userAgent)
  }

  /** Ez egy Apple eszköz? */
  public get isApple (): boolean {
    return isApple(this.userAgent)
  }

  /** Ez egy IOS eszköz? */
  public get isIOS (): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isIOS(this.userAgent)
  }

  public get isIPadOS (): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isIPadOS(this.userAgent)
  }

  /** Ez egy tablet? */
  public get isTablet (): boolean {
    return isTablet(this.userAgent)
  }

  /** Ez egy mobil eszköz, vagy CORDOVA-val buildelünk? */
  public get isMobile (): boolean {
    return isMobile(this.userAgent)
  }

  /** Ez egy handheld (kézi), vagy tablet eszköz, vagy CORDOVA-val buildelünk? */
  public get isHandheld (): boolean {
    // DevTools touch mód: kézi eszközt szimulálunk.
    return devOverrides.isOn('touch') || isHandheld(this.userAgent)
  }

  /** Ez egy android eszköz, vagy CORDOVA-val buildelünk? */
  public get isAndroid (): boolean {
    return isAndroid(this.userAgent)
  }

  /** Az OS Windows? */
  public get isWindows (): boolean {
    return isWindows(this.userAgent)
  }

  /** Firefox-ot használ a user. */
  public get isFF (): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isFF(this.userAgent)
  }

  /** Chrome-ot használ a user. */
  public get isChrome (): boolean {
    return isChrome(this.userAgent)
  }

  /** Safarit használ a user. */
  public get isSafari (): boolean {
    return isSafari(this.userAgent)
  }

  /** IE böngészőt használja a user. */
  public get isIE (): boolean { // eslint-disable-line @typescript-eslint/naming-convention
    return isIE(this.userAgent)
  }

  /** Opera böngészőt használja a user. */
  public get isOpera (): boolean {
    return isOpera(this.userAgent)
  }

  /** Edge böngészőt használja a user. */
  public get isEdge (): boolean {
    return isEdge(this.userAgent)
  }

  public constructor (userAgent: string) {
    this.userAgent = userAgent
  }

  /**
   * Singleton példányosítás és példány elkérése.
   * @param ua - Opcionális UA string.
   */
  public static getInstance (ua = UserAgent.getNavigatorUserAgent()): UserAgent {
    if (!UserAgent.instance) {
      UserAgent.instance = new UserAgent(ua)
    }

    return UserAgent.instance
  }

  /** Visszaadja browseres környezetben a navigator nyers userAgent property értékét. */
  public static getNavigatorUserAgent (): string {
    const ua = is.node()
      ? ''
      : window.navigator.userAgent

    return ua
  }
}
